import useDefaultDevice from 'hooks/useDefaultDevice/useDefaultDevice'
import useFetchApi, { isLoading } from 'hooks/useFetchApi/useFetchApi'
import useReceiptLine from 'hooks/useReceiptLine/useReceiptLine'
import { PrinterContract } from 'models'
import generatePayload from 'promptpay-qr'
import { useCallback, useEffect, useMemo, useState } from 'react'
import locationService from 'Services/api/location.service'
import { PAYMENT_TYPE_ID } from 'constant/paymentType'
import { isUndefined } from 'lodash-es'
import { useFormat } from 'Services/localize/format'
import { sendPrintCommandToAndroidPrintingApp } from 'utilities/androidPrinting'
import paymentSettingService from 'Services/api/paymentSetting.service'
import useCurrentTerminalDevicesHwAgentCaller from 'hooks/useHardwareAgentCaller/useCurrentTerminalDevicesHwAgentCaller'

const usePromptpayPayment = () => {
    const [initialFetch, setInitialFetch] = useState<boolean>(true)
    const [
        setting,
        getPaymentSettingById,
        getStatus,
        ,
        ,
        setSetting,
    ] = useFetchApi(paymentSettingService.getSystemPaymentById)
    const [location, getLocationData] = useFetchApi(
        locationService.getCurrentLocation,
    )
    const { currency } = useFormat()
    const accountSettingId = useMemo(() => {
        return setting?.accountPromptPayID ?? ''
    }, [setting?.accountPromptPayID])

    const accountIDFormat = useCallback((id: string) => {
        let format = id.replaceAll('-', '')
        //if use phone number, it must be xxx-xxx-xxxx format
        if (id.length === 10) {
            return `${format.slice(0, 3)}-${format.slice(3, 6)}-${format.slice(
                6,
            )}`
            //if use ID, it must be x-xxxx-xxxxx-xx-x format
        } else if (id.length === 13)
            return `${format.slice(0, 1)}-${format.slice(1, 5)}-${format.slice(
                5,
                10,
            )}-${format.slice(10, 12)}-${format.slice(12)}`
        else return '000-000-0000'
    }, [])

    const accountName = useMemo(() => {
        return setting?.accountPromptPayName ?? ''
    }, [setting?.accountPromptPayName])

    const _genPayload = useCallback(
        (amount: number, id?: string) => {
            return generatePayload(
                accountIDFormat(id ?? accountSettingId ?? ''),
                {
                    amount,
                },
            )
        },
        [accountSettingId, accountIDFormat],
    )
    const getPromptPaySetting = useCallback(async () => {
        return await getPaymentSettingById(PAYMENT_TYPE_ID.PROMPTPAY)
    }, [getPaymentSettingById])
    const {
        enableOnScreenPrinter,
        enableAndroidPrinting,
        onScreenPrinter,
        defaultPrinter,
    } = useDefaultDevice()
    const {
        transformReceiptCommand,
        transformReceiptSvgBase64,
    } = useReceiptLine({
        onScreenPrinter,
        defaultPrinter,
    })

    const generateQRImage = useCallback(
        (amount: number, id?: string) => {
            const image = transformReceiptSvgBase64(
                `{code:${_genPayload(amount, id)}; option:qrcode,8,L}`,
            )
            return image
        },
        [_genPayload, transformReceiptSvgBase64],
    )
    const parseTemplate = useCallback(
        (amount: number, orderid: string, payload: string) => {
            return [
                location ? '{border:line;}' : undefined,
                location?.name,
                location?.address?.addressLine1,
                location?.address?.addressLine2,
                '{border:space;}',
                '~',
                `order: ${orderid}`,
                `QR Code Prompt Pay `,
                '~',
                `{code:${payload}; option:qrcode,8,L}`,
                '~',
                `Account Name: ${accountName}`,
                `Amount: ${currency(amount)}`,
                '~',
                '~',
            ]
                .filter((l) => !isUndefined(l))
                .join('\n')
        },
        [accountName, currency, location],
    )
    const parseReceiptTemplateImage = useCallback(
        (amount: number, orderid: string) => {
            const receipt = parseTemplate(
                amount,
                orderid,
                _genPayload(amount, accountSettingId),
            )

            const image = transformReceiptSvgBase64(receipt)

            return image
        },
        [
            _genPayload,
            accountSettingId,
            parseTemplate,
            transformReceiptSvgBase64,
        ],
    )
    const hardwareAgent = useCurrentTerminalDevicesHwAgentCaller()

    const parsePrintingCommand = useCallback(
        async (
            amount: number,
            orderid: string,
            overridePrinter?: PrinterContract,
        ) => {
            const receipt = parseTemplate(amount, orderid, _genPayload(amount))
            const command = await transformReceiptCommand(
                receipt,
                overridePrinter,
            )
            return command
        },
        [_genPayload, parseTemplate, transformReceiptCommand],
    )

    const printOnAndroid = useCallback(
        async (amount: number, orderid: string) => {
            const printCommand = await parsePrintingCommand(amount, orderid)
            sendPrintCommandToAndroidPrintingApp(printCommand)
        },
        [parsePrintingCommand],
    )

    const printQRPromptpay = useCallback(
        async (amount: number, orderid: string, isAndroid: boolean = false) => {
            if (isAndroid || enableAndroidPrinting) {
                await printOnAndroid(amount, orderid)
            } else if (!enableOnScreenPrinter) {
                const printCommand = await parsePrintingCommand(amount, orderid)
                await hardwareAgent.print(printCommand, 'qr-promptpay')
            }
        },
        [
            enableAndroidPrinting,
            enableOnScreenPrinter,
            printOnAndroid,
            parsePrintingCommand,
            hardwareAgent,
        ],
    )
    useEffect(() => {
        if (initialFetch)
            // ensure fetch once when call resolve react warning memory leak
            getLocationData()
        return () => {
            setInitialFetch(true)
        }
    }, [getLocationData, initialFetch])
    return {
        generatePayload: _genPayload,
        parseReceiptTemplateImage,
        printQRPromptpay,
        generateQRImage,
        getPromptPaySetting,
        setting,
        setSetting,
        isLoading: isLoading(getStatus),
        accountName,
    }
}
export default usePromptpayPayment
